// Shared state helpers for the multi-step inquiry flow.
// Kept side-effect free so it stays test/friendly.

export const emptyInquiryState = () => ({
    // routing
    inquiry_kind: "package", // package | custom
    inquiry_type: "branding", // branding | video (theme selector)

    // package selection
    package_id: "",
    selected_package: "",
    package_category: "",
    package_kind: "",
    package_price: "",
    base_price: 0,
    is_custom_scope: false,

    // addons (array of ids)
    addons: [],

    // coupon / discount (package journey only)
    coupon_code: "",
    coupon_percent: 0,

    // brand
    business_name: "",
    business_type: "",
    website_link: "",
    facebook_link: "",
    instagram_link: "",

    // vision
    goals: "",
    what_do_you_need: "",
    project_details: "",

    // investment
    budget_mode: "range", // range | custom
    estimated_budget: "",
    custom_budget: "",
    expected_timeline: "",

    // contact
    full_name: "",
    email: "",
    whatsapp_number: "",
    instagram_username: "",
    preferred_communication: "WhatsApp",
    detected_country: "",
    detected_country_iso: "",
});

// Session-storage key so back/forward + refresh keep entered data.
export const STORAGE_KEY = "pgfx-inquiry-flow-v1";

export const loadInquiryState = () => {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return { ...emptyInquiryState(), ...parsed };
    } catch (_e) {
        return null;
    }
};

export const persistInquiryState = (state) => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_e) { /* ignore quota errors */ }
};

export const clearInquiryState = () => {
    try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
};

// --- Step registries -----------------------------------------------------
// Package Journey — the selected package IS the investment declaration, so the
// old "Investment" step has been reduced to a Timeline-only step. Custom Journey
// still needs its own Budget step (see CUSTOM_STEPS below).
export const PACKAGE_STEPS = [
    { id: "route", label: "Route", subtitle: "Choose your path" },
    { id: "package", label: "Package", subtitle: "Select what fits" },
    { id: "addons", label: "Add-ons", subtitle: "Enhance scope" },
    { id: "brand", label: "Brand", subtitle: "Your business" },
    { id: "vision", label: "Vision", subtitle: "Where you're going" },
    { id: "timeline", label: "Timeline", subtitle: "When you need it" },
    { id: "contact", label: "Contact", subtitle: "How to reach you" },
    { id: "review", label: "Review", subtitle: "Confirm & send" },
];

// Custom Project Journey — no package/add-ons steps; services + budget instead.
export const CUSTOM_STEPS = [
    { id: "route", label: "Route", subtitle: "Choose your path" },
    { id: "services", label: "Services", subtitle: "What you need" },
    { id: "brand", label: "Brand", subtitle: "Your business" },
    { id: "vision", label: "Vision", subtitle: "Where you're going" },
    { id: "budget", label: "Investment", subtitle: "Budget & timeline" },
    { id: "contact", label: "Contact", subtitle: "How to reach you" },
    { id: "review", label: "Review", subtitle: "Confirm & send" },
];

// Preview list surfaced on the Route step for the Custom Journey.
export const CUSTOM_STEPS_PREVIEW = CUSTOM_STEPS;

export const isEmail = (v) => /^\S+@\S+\.\S+$/.test((v || "").trim());

// Per-step validators — return object of errors keyed by field.
export const validateStep = (stepId, data) => {
    const e = {};
    switch (stepId) {
        case "route":
            if (!data.inquiry_kind) e._step = "Choose Package Inquiry or Custom Project to continue.";
            break;
        case "package":
            if (!data.selected_package && !data.is_custom_scope) {
                e._step = "Select a package to continue.";
            }
            break;
        case "addons":
            // Add-ons are optional; no required validation.
            break;
        case "services":
            if (!(data.services_needed || []).length) {
                e._step = "Pick at least one service so we can shape a proposal.";
            }
            break;
        case "brand":
            // Business fields are optional but encouraged.
            break;
        case "vision":
            if (!(data.goals || "").trim() && !(data.project_details || "").trim()) {
                e._step = "Share your goals or a few sentences about the project.";
            }
            break;
        case "timeline":
            // Package Journey — timeline only (package IS the investment).
            if (!data.expected_timeline) e.expected_timeline = "Pick a timeline so we can plan resourcing.";
            break;
        case "investment":
        case "budget":
            // Custom Journey (budget) — legacy "investment" kept for defensive
            // safety in case any deep link/state still references it.
            if (data.budget_mode === "range") {
                if (!data.estimated_budget) e.estimated_budget = "Pick a budget range or switch to 'Type my own'.";
            } else {
                if (!(data.custom_budget || "").trim()) e.custom_budget = "Please enter a target budget.";
            }
            if (!data.expected_timeline) e.expected_timeline = "Pick a timeline so we can plan resourcing.";
            break;
        case "contact":
            if (!(data.full_name || "").trim()) e.full_name = "Please enter your full name.";
            if (!(data.email || "").trim()) e.email = "Email is required.";
            else if (!isEmail(data.email)) e.email = "Please enter a valid email address.";
            if (!(data.whatsapp_number || "").trim() && !(data.instagram_username || "").trim()) {
                e.contact = "Share at least one of WhatsApp or Instagram so we can reach you.";
            }
            break;
        case "review":
            // Composite check — re-runs prior validators to be safe.
            {
                const kindKey = data.inquiry_kind === "custom" ? "services" : "package";
                const investmentKey = data.inquiry_kind === "custom" ? "budget" : "timeline";
                const composite = [
                    validateStep(kindKey, data),
                    validateStep(investmentKey, data),
                    validateStep("contact", data),
                ];
                composite.forEach((c) => Object.assign(e, c));
            }
            break;
        default:
            break;
    }
    return e;
};
